import { P, match } from "ts-pattern";
import type { AppUser, Person } from "../types/appUser";
import { RULE_MATCHING, type RuleMatching } from "../types/ruleMatch";

export type RuleMatchUsersItem = {
  ruleMatching: RuleMatching;
  user?: AppUser;
  worker?: Person;
};

/**
 * 与えられたアイテムがどのルールに適合するかを判定して返します
 * どのルールにもマッチしない場合は undefined を返します。
 *
 * @param item 判定対象のアイテム
 * @returns マッチしたルールタイプ（RuleMatching）
 */
export const determineRuleMatching = (
  item: RuleMatchUsersItem,
): RuleMatching | undefined => {
  return (
    match(item)
      // MATCHED, VIOLATED, VIOLATED_ROLE のケース（userとworker両方必要）
      .with(
        {
          ruleMatching: P.union(
            RULE_MATCHING.MATCHED,
            RULE_MATCHING.VIOLATED,
            RULE_MATCHING.VIOLATED_ROLE,
          ),
          user: P.not(P.nullish),
          worker: P.not(P.nullish),
        },
        ({ ruleMatching }) => ruleMatching,
      )
      // NO_WORKER のケース（userのみ必要）
      .with(
        {
          ruleMatching: RULE_MATCHING.NO_WORKER,
          user: P.not(P.nullish),
        },
        ({ ruleMatching }) => ruleMatching,
      )
      // NO_USER のケース（workerのみ必要）
      .with(
        {
          ruleMatching: RULE_MATCHING.NO_USER,
          worker: P.not(P.nullish),
        },
        ({ ruleMatching }) => ruleMatching,
      )
      // マッチしない場合
      .otherwise(() => RULE_MATCHING.UNDEFINED)
  );
};

export const isMatched = (item: RuleMatchUsersItem): boolean => {
  return match(item)
    .with(
      {
        ruleMatching: RULE_MATCHING.MATCHED,
        user: P.not(P.nullish),
        worker: P.not(P.nullish),
      },
      () => true,
    )
    .otherwise(() => false);
};

export const isViolated = (item: RuleMatchUsersItem): boolean => {
  return match(item)
    .with(
      {
        ruleMatching: RULE_MATCHING.VIOLATED,
        user: P.not(P.nullish),
        worker: P.not(P.nullish),
      },
      () => true,
    )
    .otherwise(() => false);
};

export const isViolatedRole = (item: RuleMatchUsersItem): boolean => {
  return match(item)
    .with(
      {
        ruleMatching: RULE_MATCHING.VIOLATED_ROLE,
        user: P.not(P.nullish),
        worker: P.not(P.nullish),
      },
      () => true,
    )
    .otherwise(() => false);
};

export const isNoWorker = (item: RuleMatchUsersItem): boolean => {
  return match(item)
    .with(
      {
        ruleMatching: RULE_MATCHING.NO_WORKER,
        user: P.not(P.nullish),
      },
      () => true,
    )
    .otherwise(() => false);
};

export const isNoUser = (item: RuleMatchUsersItem): boolean => {
  return match(item)
    .with(
      {
        ruleMatching: RULE_MATCHING.NO_USER,
        worker: P.not(P.nullish),
      },
      () => true,
    )
    .otherwise(() => false);
};
